import { format } from "date-fns"
import type { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import { getDateFnsLocale } from "@/lib/i18n/format"
import { formatFuelFraction } from "./verdictDisplay"
import { parseUtcIso } from "./parseUtcIso"
import type { ChipEvent } from "./fuelReportSummary"

// Light-mode colors sampled from the design mock; dark mode keeps the
// theme palette.
const TONE_CLASSES = {
  green: "bg-[#c8f6d3] text-[#3c7e44] dark:bg-green-950 dark:text-green-300",
  red: "bg-[#f6cccb] text-[#aa2e26] dark:bg-red-950 dark:text-red-300",
  gray: "bg-[#e5e5e5] text-[#3f3f46] dark:bg-muted dark:text-muted-foreground",
} as const

const BASE =
  "inline-flex items-center whitespace-nowrap rounded-md px-2.5 py-1 text-[13px] font-medium"

function chipLabel(event: ChipEvent, showDate: boolean): string {
  const d = parseUtcIso(event.at)
  const time = Number.isNaN(d.getTime()) ? "--:--" : format(d, "HH:mm")
  const prefix =
    showDate && !Number.isNaN(d.getTime())
      ? `${format(d, "EEE d", { locale: getDateFnsLocale() })} · `
      : ""
  const { fuelBefore, fuelAfter } = event.refuel
  return `${prefix}${time} · ${formatFuelFraction(fuelBefore)} → ${formatFuelFraction(fuelAfter)}`
}

function chipTitle(t: TFunction, event: ChipEvent): string {
  if (event.tone === "green") return t("reports.fuelChip.matched")
  if (event.tone === "red") return t("reports.fuelChip.elsewhere")
  return t("reports.fuelChip.noAlert")
}

export function FuelTimelineChip({
  event,
  showDate,
  onClick,
}: {
  event: ChipEvent
  showDate: boolean
  onClick: () => void
}) {
  const { t } = useTranslation()
  const label = chipLabel(event, showDate)
  const title = chipTitle(t, event)
  const classes = `${BASE} ${TONE_CLASSES[event.tone]}`
  return (
    <button
      onClick={onClick}
      title={title}
      className={`${classes} hover:opacity-80`}
    >
      {label}
    </button>
  )
}
